import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useLocation } from "react-router";

import type { PortfolioPermissionsDto } from "@/api/types";
import { AlertTriangleIcon } from "@/components/Icons/AlertTriangleIcon";
import { DotsGridIcon } from "@/components/Icons/DotsGridIcon";
import { MessageChatSquareIcon } from "@/components/Icons/MessageChatSquareIcon";
import { MessageSquare02Icon } from "@/components/Icons/MessageSquare02Icon";
import { PieChart01Icon } from "@/components/Icons/PieChart01Icon";
import { Settings01Icon } from "@/components/Icons/Settings01Icon";
import { SwitchHorizontal01Icon } from "@/components/Icons/SwitchHorizontal01Icon";
import { Tool02Icon } from "@/components/Icons/Tool02Icon";
import { Users01Icon } from "@/components/Icons/Users01Icon";
import { addPortfolioProjectFilterQueryParams } from "@/components/ProjectFilter/portfolioProjectFilterQueryParams";
import type { SidebarBaseItemType, SidebarGroupedItemType, SidebarItemType } from "@/components/Sidebar/types";
import { hasAdminProject } from "@/helpers/portfolio-access";
import { SIGNALR_EVENTS } from "@/helpers/signalr";
import { useSessionUserOptional } from "@/hooks/Network/useSessionUser";
import { usePermission } from "@/hooks/usePermission";
import { useSignalRHub, useSignalRSubscription } from "@/hooks/useSignalR";
import { useConfig } from "@/providers/config/useConfig";
import { usePortfolioChatQueries } from "@/queries/portfolio-chat/queryOptions";
import { usePortfolioQueries } from "@/queries/portfolio/queryOptions";
import { useSelfAccountQueries } from "@/queries/self-account/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { BarChart01Icon } from "../Icons/BarChart01Icon";
import { Upload01Icon } from "../Icons/Upload01Icon";
import { canAccessSystemSettings, canAccessUserSearch } from "./permissions";

interface PortfolioAnalyticsNavigationItemOptions {
  isCommunityFeedSentimentAvailable: boolean;
  isExploreMoreNavigationAvailable: boolean;
  labels: {
    analytics: string;
    bookings: string;
    communityFeed: string;
    events: string;
    exploreMore: string;
    residentSentiment: string;
    tickets: string;
  };
  withProjectFilterQueryParams: (path: string) => string;
}

interface PortfolioNavigationAvailabilityOptions {
  canAccessSystemSettings: boolean;
  canAccessUserSearch: boolean;
  canViewPortfolio: boolean;
  isPortfolioBulkUploadAddressAvailable: boolean;
  portfolioPermissions: PortfolioPermissionsDto;
}

interface PortfolioNavigationAvailability {
  addresses: boolean;
  chats: boolean;
  massMessage: boolean;
  massSurvey: boolean;
  systemSettings: boolean;
  users: boolean;
}

export function getPortfolioNavigationAvailability({
  canAccessSystemSettings,
  canAccessUserSearch,
  canViewPortfolio,
  isPortfolioBulkUploadAddressAvailable,
  portfolioPermissions,
}: PortfolioNavigationAvailabilityOptions): PortfolioNavigationAvailability {
  return {
    addresses: portfolioPermissions.canMassCreateAddresses && isPortfolioBulkUploadAddressAvailable,
    chats: canViewPortfolio,
    massMessage: portfolioPermissions.canMassMessageGeneralPost || portfolioPermissions.canMassMessageAnnouncementPost,
    massSurvey: portfolioPermissions.canMassSurvey,
    systemSettings: canAccessSystemSettings,
    users: canAccessUserSearch || portfolioPermissions.canInviteAdmins,
  };
}

/**
 * Builds the portfolio analytics navigation group from localized labels.
 * Standard analytics destinations are included independently; feature flags control sentiment and Explore more.
 * The supplied query-parameter callback is applied to every destination so portfolio filters survive navigation.
 *
 * @returns The grouped sidebar item containing the available analytics destinations.
 */
export function createPortfolioAnalyticsNavigationItem({
  isCommunityFeedSentimentAvailable,
  isExploreMoreNavigationAvailable,
  labels,
  withProjectFilterQueryParams,
}: PortfolioAnalyticsNavigationItemOptions): SidebarGroupedItemType {
  const items: SidebarBaseItemType[] = [
    {
      key: "portfolio-analytics-community-feed",
      label: labels.communityFeed,
      to: withProjectFilterQueryParams(routes.portfolio.analyticsCommunityFeed()),
      exact: true,
      "data-testid": "navigation-portfolio-analytics-community-feed",
    },
  ];

  if (isCommunityFeedSentimentAvailable) {
    items.push({
      key: "portfolio-analytics-sentiment",
      label: labels.residentSentiment,
      to: withProjectFilterQueryParams(routes.portfolio.analyticsSentiment()),
      "data-testid": "navigation-portfolio-analytics-sentiment",
    });
  }

  items.push(
    {
      key: "portfolio-analytics-tickets",
      label: labels.tickets,
      to: withProjectFilterQueryParams(routes.portfolio.analyticsTickets()),
      "data-testid": "navigation-portfolio-analytics-tickets",
    },
    {
      key: "portfolio-analytics-events",
      label: labels.events,
      to: withProjectFilterQueryParams(routes.portfolio.analyticsEvents()),
      "data-testid": "navigation-portfolio-analytics-events",
    },
    {
      key: "portfolio-analytics-bookings",
      label: labels.bookings,
      to: withProjectFilterQueryParams(routes.portfolio.analyticsBookings()),
      "data-testid": "navigation-portfolio-analytics-bookings",
    },
  );

  if (isExploreMoreNavigationAvailable) {
    items.push({
      key: "portfolio-analytics-explore",
      label: labels.exploreMore,
      to: withProjectFilterQueryParams(routes.portfolio.analyticsExplore()),
      "data-testid": "navigation-portfolio-analytics-explore",
    });
  }

  return {
    key: "analytics-insights",
    label: labels.analytics,
    icon: <PieChart01Icon />,
    to: withProjectFilterQueryParams(routes.portfolio.analyticsOverview()),
    "data-testid": "navigation-portfolio-analytics",
    type: "group",
    items,
  };
}

export const usePortfolioSidebarManager = (): {
  navigationItems: SidebarItemType[];
  hasMultipleAdminProjects: boolean;
} => {
  const { t } = useTranslation();
  const location = useLocation();
  const [signalRUnreadPortfolioChats, setSignalRUnreadPortfolioChats] = useState<number | undefined>(undefined);
  const [user] = useSessionUserOptional({
    keepPreviousData: true,
  });
  const isPortfolioBulkUploadAddressAvailable = useConfig("isPortfolioBulkUploadAddressAvailable");
  const isCommunityFeedSentimentAvailable = useConfig("isCommunityFeedSentimentAvailable");
  // The legacy config name is broader than its current purpose: it controls only whether
  // Explore more is discoverable in navigation, not standard navigation or route availability.
  const isExploreMoreNavigationAvailable = useConfig("isAnalyticsInsightsAvailable");

  const selfAccountQueries = useSelfAccountQueries();
  const {
    data: linkedProjects,
    isPending: isPendingLinkedProjects,
    isError: isErrorLinkedProjects,
  } = useQuery(selfAccountQueries.listLinkedProjects());

  const linkedAdminProjects = linkedProjects?.filter((x) => x.userRole !== "resident") ?? [];
  const canViewPortfolio = hasAdminProject(linkedProjects);
  const hasMultipleAdminProjects = linkedAdminProjects.length > 1;
  const hasPermission = usePermission();
  const onPortfolioChatUnreadCountChanged = useCallback(
    (
      ...args: [
        {
          unreadChats: number;
          fromConnectionStarted: boolean;
        },
      ]
    ) => {
      setSignalRUnreadPortfolioChats(args[0].unreadChats);
    },
    [],
  );
  const { signalRConnection } = useSignalRHub("global-hub", {
    disabled: !user || !canViewPortfolio,
  });
  useSignalRSubscription(
    signalRConnection,
    SIGNALR_EVENTS.CHAT.UPDATE_PORTFOLIO_CHAT_UNREAD_COUNT,
    onPortfolioChatUnreadCountChanged,
  );

  const portfolioQueries = usePortfolioQueries();
  const portfolioChatQueries = usePortfolioChatQueries();
  const withProjectFilterQueryParams = useCallback(
    (path: string) => addPortfolioProjectFilterQueryParams(path, location.search),
    [location.search],
  );
  const {
    data: portfolioPermissions,
    isPending: isPendingPortfolioPermissions,
    isError: isErrorPortfolioPermissions,
  } = useQuery(portfolioQueries.permissions());
  const { data: unreadPortfolioChats } = useQuery({
    ...portfolioChatQueries.list({
      UnreadOnly: true,
      Limit: 1,
    }),
    enabled: Boolean(user && canViewPortfolio),
  });
  const amountUnreadPortfolioChats = signalRUnreadPortfolioChats ?? unreadPortfolioChats?.total ?? 0;

  if (
    isPendingLinkedProjects ||
    isErrorLinkedProjects ||
    isPendingPortfolioPermissions ||
    isErrorPortfolioPermissions ||
    !user
  ) {
    return {
      navigationItems: [],
      hasMultipleAdminProjects: false,
    };
  }

  const items: (SidebarBaseItemType | SidebarGroupedItemType)[] = [
    {
      key: "portfolio",
      label: t("navigation.portfolio.portfolio"),
      icon: <DotsGridIcon />,
      to: withProjectFilterQueryParams(routes.portfolio.overview()),
      exact: true,
      "data-testid": "navigation-portfolio",
    },
  ];

  items.push(
    createPortfolioAnalyticsNavigationItem({
      isCommunityFeedSentimentAvailable,
      isExploreMoreNavigationAvailable,
      labels: {
        analytics: t("navigation.portfolio.analytics-insights"),
        bookings: t("navigation.portfolio.analytics-bookings"),
        communityFeed: t("page.analytics-community-feed.breadcrumb"),
        events: t("page.analytics-events.breadcrumb"),
        exploreMore: t("page.analytics-navigation.explore-all"),
        residentSentiment: t("page.analytics-community-feed.sentiment.breadcrumb"),
        tickets: t("page.analytics-tickets.breadcrumb"),
      },
      withProjectFilterQueryParams,
    }),
  );

  const canAccessUserSearchInPortfolio = hasPermission(canAccessUserSearch);
  const navigationAvailability = getPortfolioNavigationAvailability({
    canAccessSystemSettings: hasPermission(canAccessSystemSettings),
    canAccessUserSearch: canAccessUserSearchInPortfolio,
    canViewPortfolio,
    isPortfolioBulkUploadAddressAvailable,
    portfolioPermissions,
  });

  if (navigationAvailability.massMessage) {
    items.push({
      key: "mass-message",
      label: t("navigation.portfolio.mass-message"),
      icon: <MessageSquare02Icon />,
      "data-testid": "navigation-mass-message",
      to: routes.portfolio.createMassMessage(),
    });
  }

  if (navigationAvailability.massSurvey) {
    items.push({
      key: "mass-survey",
      label: t("navigation.portfolio.mass-survey"),
      icon: <BarChart01Icon />,
      "data-testid": "navigation-mass-survey",
      to: routes.portfolio.createMassSurvey(),
    });
  }

  if (linkedAdminProjects.some((x) => x.userRole !== "servicePartner")) {
    items.push({
      key: "tickets",
      label: t("navigation.portfolio.tickets"),
      icon: <Tool02Icon />,
      "data-testid": "navigation-portfolio-tickets",
      to: withProjectFilterQueryParams(routes.portfolio.tickets()),
    });
  }

  if (navigationAvailability.chats) {
    items.push({
      key: "chats",
      label: t("navigation.portfolio.chats"),
      icon: <MessageChatSquareIcon />,
      "data-testid": "navigation-portfolio-chats",
      to: routes.portfolio.chats(),
      isNewEventAvailable: amountUnreadPortfolioChats > 0,
      newEventCount: amountUnreadPortfolioChats,
    });
  }

  if (linkedAdminProjects.some((x) => x.userRole !== "servicePartner")) {
    items.push({
      key: "alerts",
      label: t("navigation.portfolio.alerts"),
      icon: <AlertTriangleIcon />,
      "data-testid": "navigation-alerts",
      to: routes.alerts.list(),
    });
  }

  if (navigationAvailability.addresses) {
    items.push({
      key: "addresses",
      label: t("navigation.portfolio.address-bulk-upload"),
      icon: <Upload01Icon />,
      "data-testid": "navigation-address-bulk-upload",
      to: routes.portfolio.bulkUploadAddresses(),
    });
  }

  if (navigationAvailability.users) {
    const subItemsUser: SidebarBaseItemType[] = [];

    if (canAccessUserSearchInPortfolio) {
      subItemsUser.push({
        key: "user-search",
        label: t("navigation.portfolio.user-search"),
        "data-testid": "navigation-user-search",
        to: routes.portfolio.userLookup(),
      });
    }

    if (portfolioPermissions.canInviteAdmins) {
      subItemsUser.push({
        key: "user-add-admin",
        label: t("navigation.portfolio.add-admins"),
        "data-testid": "navigation-user-add-admin",
        to: routes.portfolio.addAdmin(),
      });
    }

    items.push({
      key: "users",
      label: "Users",
      icon: <Users01Icon />,
      "data-testid": "navigation-users",
      type: "group",
      items: subItemsUser,
    });
  }

  items.push({
    key: "integrations",
    label: t("navigation.portfolio.integrations"),
    icon: <SwitchHorizontal01Icon />,
    "data-testid": "navigation-integrations",
    to: routes.portfolio.integrations(),
  });

  if (navigationAvailability.systemSettings) {
    items.push({
      key: "system-settings-group",
      label: t("navigation.portfolio.system-settings"),
      "data-testid": "navigation-system-settings-group",
      icon: <Settings01Icon />,
      type: "group",
      items: [
        {
          key: "project-connections",
          label: t("navigation.portfolio.project-connections"),
          to: routes.projectConnections.list(),
          "data-testid": "navigation-project-connections",
        },
        {
          key: "platform-groups",
          label: t("navigation.portfolio.platform-groups"),
          to: routes.platformGroups.list(),
          "data-testid": "navigation-platform-groups",
        },
        {
          key: "automated-surveys",
          label: t("navigation.portfolio.automated-surveys"),
          to: routes.automatedSurveys.list(),
          "data-testid": "navigation-automated-surveys",
        },
        {
          key: "new-project",
          label: t("navigation.portfolio.new-project"),
          to: routes.portfolio.newProject(),
          "data-testid": "navigation-new-project",
        },
      ],
    });
  }

  return {
    navigationItems: items,
    hasMultipleAdminProjects,
  };
};
